"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

export default function CreateSlotPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'stationId',
      label: t('terminal_scheduling.fields.stationId', 'Station'),
      type: 'text',
      required: true,
      placeholder: 'Station UUID',
    },
    {
      id: 'slotType',
      label: t('terminal_scheduling.fields.slotType', 'Slot Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'arrival', label: t('terminal_scheduling.slots.type.arrival', 'Arrival') },
        { value: 'departure', label: t('terminal_scheduling.slots.type.departure', 'Departure') },
        { value: 'shunting', label: t('terminal_scheduling.slots.type.shunting', 'Shunting') },
      ],
    },
    {
      id: 'trackNumber',
      label: t('terminal_scheduling.fields.trackNumber', 'Track Number'),
      type: 'number',
      required: true,
    },
    {
      id: 'platform',
      label: t('terminal_scheduling.fields.platform', 'Platform'),
      type: 'text',
    },
    {
      id: 'startTime',
      label: t('terminal_scheduling.fields.startTime', 'Start Time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'endTime',
      label: t('terminal_scheduling.fields.endTime', 'End Time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'trainPlanId',
      label: t('terminal_scheduling.fields.trainPlanId', 'Train Plan ID'),
      type: 'text',
      placeholder: 'Optional UUID',
    },
    {
      id: 'cargoDescription',
      label: t('terminal_scheduling.fields.cargoDescription', 'Cargo Description'),
      type: 'textarea',
    },
    {
      id: 'notes',
      label: t('terminal_scheduling.fields.notes', 'Notes'),
      type: 'textarea',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'location',
      title: t('terminal_scheduling.groups.location', 'Location'),
      fields: ['stationId', 'trackNumber', 'platform'],
    },
    {
      id: 'timing',
      title: t('terminal_scheduling.groups.timing', 'Timing'),
      fields: ['slotType', 'startTime', 'endTime'],
    },
    {
      id: 'details',
      title: t('terminal_scheduling.groups.details', 'Details'),
      fields: ['trainPlanId', 'cargoDescription', 'notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('terminal_scheduling.create.title', 'New Terminal Slot')}
          backHref="/backend/terminal-scheduling"
          fields={fields}
          groups={groups}
          submitLabel={t('terminal_scheduling.slots.actions.create', 'New Slot')}
          cancelHref="/backend/terminal-scheduling"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('terminal_scheduling/slots', payload)
            flash(t('terminal_scheduling.slots.flash.created', 'Terminal slot created'), 'success')
            router.push(`/backend/terminal-scheduling/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
